import { Container } from './Container'
import { Breadcrumb } from '@/components/ui/breadcrumb'
import { cn } from '@/lib/utils'

interface Crumb {
  label: string
  href?: string
}

interface PageHeroProps {
  title: string
  subtitle?: string
  crumbs?: Crumb[]
  compact?: boolean
}

export function PageHero({ title, subtitle, crumbs, compact }: PageHeroProps) {
  return (
    <section
      className={cn(
        'relative overflow-hidden border-b border-primary-200/70 bg-linear-to-br from-[#d4e4f4] via-primary-50/80 to-white',
        compact ? 'py-3 md:py-6' : 'py-4 md:py-6',
      )}
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute inset-y-0 left-0 w-[38%] bg-linear-to-r from-qbc-navy/12 via-primary-600/5 to-transparent" />
        {!compact && (
          <>
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_75%_110%_at_100%_-15%,rgba(11,42,92,0.2),transparent_52%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_75%_at_0%_100%,rgba(7,26,58,0.14),transparent_48%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_62%_55%,rgba(15,93,154,0.08),transparent_40%)]" />
            <div className="absolute -right-10 -top-12 h-36 w-36 rounded-full bg-primary-400/20 blur-2xl" />
            <div className="absolute -bottom-12 left-[8%] h-28 w-28 rounded-full bg-qbc-navy/10 blur-2xl" />
            <div className="absolute right-[14%] top-2 h-14 w-14 rounded-full border border-qbc-navy/10 bg-linear-to-br from-white/90 to-primary-100/50 shadow-sm" />
            <div className="absolute bottom-2 left-[36%] h-8 w-8 rotate-12 rounded-md border border-qbc-navy/10 bg-qbc-navy/5" />
            <div className="absolute right-[32%] bottom-3 h-5 w-5 rounded-full bg-primary-600/15" />
          </>
        )}
        <div className="absolute bottom-0 left-0 h-0.5 w-full bg-linear-to-r from-qbc-navy/20 via-primary-500/35 to-primary-300/15" />
      </div>

      <Container className="relative">
        {crumbs && crumbs.length > 0 && (
          <Breadcrumb items={crumbs} className="mb-1.5 text-[11px] sm:text-sm" />
        )}
        <h1
          className={cn(
            'font-display font-bold tracking-tight text-qbc-navy',
            compact ? 'text-base md:text-2xl' : 'text-lg md:text-2xl',
          )}
        >
          {title}
        </h1>
        {subtitle && (
          <p className={cn('max-w-2xl text-neutral-700', compact ? 'mt-1 text-xs md:text-sm' : 'mt-1 text-sm')}>
            {subtitle}
          </p>
        )}
      </Container>
    </section>
  )
}
